import { useCallback, useRef } from "react";
import {
  observeDwell,
  useContentProgress,
} from "../../tools/analytics/useContentProgress";
import type { ContentExitAction } from "../../tools/analytics/base.analytics";

// The attribute each drawn item of the list carries, valued with its id.
export const PLAYLIST_ITEM_ATTRIBUTE = "data-progress-item";

/**
 * The open of a playlist: the share of its drawn items seen, each one at least
 * `SEEN_AFTER_MS` on screen, and the items opened from it. Like every open,
 * it reports nothing before `SETTLE_MS`. `playlistId` is null until the list
 * is drawn, so the open starts with its items.
 */
export function usePlaylistProgress(playlistId: string | null): {
  listRef: (node: HTMLElement | null) => void;
  noteItemOpened: (itemId: string) => void;
  noteExit: (action: ContentExitAction) => void;
} {
  const listNodeRef = useRef<HTMLElement | null>(null);
  const dwellRef = useRef<ReturnType<typeof observeDwell> | null>(null);
  // Kept across a return from the background: the items stay seen.
  const seenRef = useRef(new Set<string>());
  const openedRef = useRef(new Set<string>());
  const settledRef = useRef(false);

  /** The items drawn now, and those of them seen: a card removed from an
   * edited collection leaves both counts. */
  function itemCounts(): { total: number; seen: number } {
    const node = listNodeRef.current;
    if (node === null) return { total: 0, seen: 0 };
    const drawn = new Set(
      Array.from(node.querySelectorAll(`[${PLAYLIST_ITEM_ATTRIBUTE}]`)).map(
        (element) => element.getAttribute(PLAYLIST_ITEM_ATTRIBUTE)
      )
    );
    return {
      total: drawn.size,
      seen: Array.from(seenRef.current).filter((key) => drawn.has(key)).length,
    };
  }

  function reportSeen() {
    if (!settledRef.current) return;
    const { total, seen } = itemCounts();
    if (total === 0) return;
    progress.reportPercent((seen / total) * 100);
  }

  function watch(node: HTMLElement) {
    if (dwellRef.current !== null) dwellRef.current.disconnect();
    dwellRef.current = observeDwell(node, PLAYLIST_ITEM_ATTRIBUTE, (key) => {
      seenRef.current.add(key);
      reportSeen();
    });
  }

  const progress = useContentProgress({
    subject:
      playlistId === null
        ? null
        : { contentType: "playlist", contentId: playlistId },
    endOnHidden: true,
    onStart: (start) => {
      settledRef.current = false;
      if (start === "new") {
        seenRef.current = new Set();
        openedRef.current = new Set();
      }
      const node = listNodeRef.current;
      if (node === null) {
        throw new Error("should not happen: a playlist open with no list");
      }
      watch(node);
    },
    onSettled: () => {
      settledRef.current = true;
      reportSeen();
    },
    onFinish: () => {
      if (dwellRef.current !== null) dwellRef.current.disconnect();
      dwellRef.current = null;
      const { total, seen } = itemCounts();
      return {
        mediaDurationSeconds: null,
        playlistItems: { total, seen, opened: openedRef.current.size },
        article: null,
      };
    },
  });

  const watchRef = useRef(watch);
  watchRef.current = watch;
  const listRef = useCallback((node: HTMLElement | null) => {
    if (node === null || node === listNodeRef.current) return;
    listNodeRef.current = node;
    // A list drawn again under an open (a reload of its rows) is watched
    // anew; before the open starts, `onStart` watches it.
    if (dwellRef.current !== null) watchRef.current(node);
  }, []);

  const noteItemOpened = useCallback(
    (itemId: string) => {
      openedRef.current.add(itemId);
      progress.noteExit("next_content");
    },
    [progress.noteExit]
  );

  return { listRef, noteItemOpened, noteExit: progress.noteExit };
}
